import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  fetchBillingAccountInfo,
  fetchSubscriptionInfo,
} from 'services/ctp/ctpAsync';
import {
  selectIsBillingAccountDetailsExist,
  selectIsProprietaryUser,
  selectIsSubscriptionDetailsExist,
  selectSubscriptionDetails,
} from 'services/ctp/ctpSelectors';
import {
  selectIsNonPrimaryUser,
  selectUserBillable,
} from 'services/user/userSelectors';
import {
  selectCardExpiryModalRequested,
  selectCardExpiryModalSkipToPayment,
} from 'services/misc/miscSelectors';
import { setCardExpiryModalRequested } from 'services/misc/miscSlice';
import isCardExpiringSoon from 'utils/cardExpiry';
import { logErrors } from 'utils';
import useIsTourBlocking from 'pages/dashboard/tour/useIsTourBlocking';

/* Session-scoped flag so a user who dismisses the modal doesn't
 * see it again on every page remount within the same tab. Cleared
 * on tab close, matching Angular's ngrx-in-memory
 * `cardExpiryReminderSkipped` behaviour. */
const SKIP_KEY = 'cardExpiryReminderSkipped';

/* Angular parity — three triggers, checked in priority order:
 *
 *   1. Explicit request from the Discover proprietary banner click
 *      (`requestCardExpiryModalOnPayment()`). Bypasses the
 *      session-skip flag so a re-click always opens the modal.
 *
 *   2. Discover-employee case — `dashboard-main.component.ts:
 *      556-578`. Opens the same modal (proprietary variant) when
 *      the user is still on the legacy Discover proprietary card
 *      (`selectIsProprietaryUser`). Does NOT depend
 *      on `isCardExpiringSoon` — those users typically have no
 *      Zuora subscription at all.
 *
 *   3. Standard case — `dashboard-main.component.ts:402-425`. Any
 *      primary + billable user: fetch subscription info and open
 *      the modal when the saved card's end-of-month expiry is
 *      within 90 days (`isCardExpiringSoon`).
 *
 * Mounted at layout level via CardExpiryOnDemand so a banner click
 * from any authenticated page opens the modal without a redirect
 * through /dashboard. */
function useCardExpiryReminder() {
  const dispatch = useAppDispatch();
  const isBillable = useAppSelector(selectUserBillable);
  const isNonPrimary = useAppSelector(selectIsNonPrimaryUser);
  const subscription = useAppSelector(selectSubscriptionDetails);
  const hasSubscriptionLoaded = useAppSelector(
    selectIsSubscriptionDetailsExist,
  );
  const hasBillingAccountLoaded = useAppSelector(
    selectIsBillingAccountDetailsExist,
  );
  const isDiscoverProprietary = useAppSelector(selectIsProprietaryUser);
  const isModalRequested = useAppSelector(selectCardExpiryModalRequested);
  const skipToPayment = useAppSelector(selectCardExpiryModalSkipToPayment);
  /* Product rule: the dashboard tour goes first. Auto-open waits;
   * an explicit banner click (`isModalRequested`) still opens. */
  const isTourBlocking = useIsTourBlocking();
  const [isOpen, setIsOpen] = useState(false);

  /* Angular gates the check on `user.type === 'primary' && user.billable`.
   * React's selectors mirror both. */
  const eligibleUser = !isNonPrimary && isBillable;

  /* Standard trigger — fetch the subscription only for primary
   * billable users. */
  useEffect(() => {
    if (!eligibleUser) return;
    if (hasSubscriptionLoaded) return;
    dispatch(fetchSubscriptionInfo()).catch(logErrors);
  }, [eligibleUser, hasSubscriptionLoaded, dispatch]);

  /* Discover trigger — fetch the billing account for every primary
   * user (Angular's `getAccountInfo()` isn't gated on billable).
   * Cheap: dedupes via `hasBillingAccountLoaded`. */
  useEffect(() => {
    if (isNonPrimary) return;
    if (hasBillingAccountLoaded) return;
    dispatch(fetchBillingAccountInfo()).catch(logErrors);
  }, [isNonPrimary, hasBillingAccountLoaded, dispatch]);

  useEffect(() => {
    /* Explicit request from the Discover proprietary banner CTA
     * (Angular parity — the click handler on the banner opens this
     * modal). Bypasses the session-skip flag so a user who already
     * closed the auto-open can still trigger the modal by clicking
     * the banner. */
    if (isModalRequested) {
      setIsOpen(true);
      return;
    }
    /* Hold auto-open while the tour owns `/dashboard`. Clearing
     * `isOpen` here does not write SKIP_KEY, so the same effect
     * re-opens the modal when `isTourBlocking` flips false. */
    if (isTourBlocking) {
      setIsOpen(false);
      return;
    }
    if (sessionStorage.getItem(SKIP_KEY) === 'true') return;
    /* Discover proprietary case — no expiry check, just missing
     * billing account triggers the modal. Evaluated first because
     * these users typically have no subscription for the standard
     * check to look at. */
    if (isDiscoverProprietary) {
      setIsOpen(true);
      return;
    }
    /* Standard expiring-card case. */
    if (!eligibleUser) return;
    if (!hasSubscriptionLoaded) return;
    const month = subscription?.creditCard?.expirationMonth;
    const year = subscription?.creditCard?.expirationYear;
    if (isCardExpiringSoon(month, year)) {
      setIsOpen(true);
    }
  }, [
    isModalRequested,
    isTourBlocking,
    isDiscoverProprietary,
    eligibleUser,
    hasSubscriptionLoaded,
    subscription,
  ]);

  const close = () => {
    sessionStorage.setItem(SKIP_KEY, 'true');
    /* Clear the request flag so a later open-then-close in the same
     * session doesn't re-fire immediately. */
    if (isModalRequested) {
      dispatch(setCardExpiryModalRequested(false));
    }
    setIsOpen(false);
  };

  return { isOpen, close, skipToPayment };
}

export default useCardExpiryReminder;
